import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useConfig } from '@context/ConfigContext';
import styles from './FAQ.module.css';

const FAQ: React.FC = () => {
  const { config } = useConfig();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  if (!config || !config.components.faq.enabled) return null;

  const { faq } = config.components;

  if (!faq.items || faq.items.length === 0) {
    return null;
  }

  const filteredItems = searchTerm
    ? faq.items.filter(
        (item) =>
          item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.answer.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : faq.items;

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: 'easeOut',
      },
    },
  };

  const highlightText = (text: string) => {
    if (!searchTerm) return text;

    const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <mark key={index} className={styles.highlight}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <section id="faq" className={styles.faq}>
      <div className={styles.container}>
        <motion.div
          className={styles.content}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Section Header */}
          <motion.div className={styles.header} variants={itemVariants}>
            <h2 className={styles.title}>Frequently Asked Questions</h2>
            <div className={styles.divider}></div>
            <p className={styles.subtitle}>
              Find answers to common questions about our services
            </p>
          </motion.div>

          {/* Search Bar */}
          {faq.searchEnabled && (
            <motion.div className={styles.searchWrapper} variants={itemVariants}>
              <div className={styles.searchBox}>
                <i className="fas fa-search" />
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={styles.searchInput}
                />
                {searchTerm && (
                  <button
                    className={styles.clearButton}
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                  >
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>
            </motion.div>
          )}

          {/* FAQ Items */}
          <motion.div className={styles.faqList} variants={itemVariants}>
            {filteredItems.length === 0 ? (
              <div className={styles.noResults}>
                <i className="fas fa-search" />
                <p>No questions found matching your search.</p>
              </div>
            ) : (
              filteredItems.map((item, index) => (
                <motion.div
                  key={index}
                  className={styles.faqItem}
                  variants={itemVariants}
                >
                  <button
                    className={`${styles.question} ${
                      openIndex === index ? styles.active : ''
                    }`}
                    onClick={() => toggleAccordion(index)}
                    aria-expanded={openIndex === index}
                    aria-controls={`faq-answer-${index}`}
                  >
                    <span className={styles.questionText}>
                      {highlightText(item.question)}
                    </span>
                    <motion.i
                      className={`fas fa-chevron-down ${styles.icon}`}
                      animate={{ rotate: openIndex === index ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    />
                  </button>

                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div
                        id={`faq-answer-${index}`}
                        className={styles.answerWrapper}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                      >
                        <div className={styles.answer}>
                          {highlightText(item.answer)}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))
            )}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;